import {ScrollView} from '@genestack/ui';
import React from 'react';
import {FoldingItem} from '../folding-item';
import {useFoldingItemsState} from '../../../../../../hooks/use-folding-items-state';
import {RightPanelListItem} from '../right-panel-list-item';
import {ProcessParamStatRecord, ProcessStatRecord} from '../interface';
import {EmptyList} from '../../../../../../components/empty-list/empty-list';
import {NothingFoundView} from './nothing-found-view';
import {matchString, SearchResultChildrenMaps} from './use-filter-stats';
import {getProcessId, getProcessParamId, getProcessParamValueId} from '../get-entity-id';
import {MatchedSynonym} from './matched-synonym';
import {ShowMoreButton} from './show-more-button';

interface Props {
    processes: ProcessStatRecord[];
    selectedGraphNodes: number[];
    setHoveredNodes: (nodeIds: number[]) => void;
    selectGraphNodes: (nodes: number[], isSelectMultiple: boolean) => void;
    searchString: string;
    searchResultChildrenMaps?: SearchResultChildrenMaps;
    foldingStateProps: ReturnType<typeof useFoldingItemsState>;
    irrelevantShownProps: ReturnType<typeof useFoldingItemsState>;
}

export function ProcessesStatistics({
    processes,
    selectedGraphNodes,
    setHoveredNodes,
    selectGraphNodes,
    searchString,
    searchResultChildrenMaps,
    foldingStateProps,
    irrelevantShownProps
}: Props) {
    function getFoldedProcessSettings(process: ProcessStatRecord) {
        if (searchString && searchResultChildrenMaps) {
            const id = getProcessId(process);
            const processNameMatches = matchString(process.label, searchString);

            const [matchedChildren, irrelevantChildren] = [
                searchResultChildrenMaps.processesMatchedChildren[id] || [],
                searchResultChildrenMaps.processesIrrelevantChildren[id] || []
            ];
            const hasShowMoreButton =
                processNameMatches && !!matchedChildren.length && !!irrelevantChildren.length;
            const isIrrelevantChildrenShown = hasShowMoreButton
                ? irrelevantShownProps.foldingState[id]
                : processNameMatches;

            return {
                renderedChildren: [
                    ...matchedChildren,
                    ...(isIrrelevantChildrenShown ? irrelevantChildren : [])
                ],
                isUnfoldedByDefault: !!matchedChildren.length,
                hasShowMoreButton
            };
        }

        return {
            renderedChildren: process.params,
            isUnfoldedByDefault: false,
            hasShowMoreButton: false
        };
    }

    function getFoldedParamSettings(process: ProcessStatRecord, param: ProcessParamStatRecord) {
        if (searchString && searchResultChildrenMaps) {
            const id = getProcessParamId(process, param);
            const parentNameMatches =
                matchString(process.label, searchString) || matchString(param.label, searchString);

            const [matchedChildren, irrelevantChildren] = [
                searchResultChildrenMaps.processParamsMatchedChildren[id] || [],
                searchResultChildrenMaps.processParamsIrrelevantChildren[id] || []
            ];
            const hasShowMoreButton =
                parentNameMatches && !!matchedChildren.length && !!irrelevantChildren.length;
            const isIrrelevantChildrenShown = hasShowMoreButton
                ? irrelevantShownProps.foldingState[id]
                : parentNameMatches;

            return {
                renderedChildren: [
                    ...matchedChildren,
                    ...(isIrrelevantChildrenShown ? irrelevantChildren : [])
                ],
                isUnfoldedByDefault: !!matchedChildren.length,
                hasShowMoreButton
            };
        }

        return {renderedChildren: param.values, isUnfoldedByDefault: false};
    }

    if (!processes.length) {
        if (searchString) {
            return <NothingFoundView searchString={searchString} />;
        }

        return <EmptyList message="Nothing found" />;
    }

    return (
        <ScrollView>
            {processes.map((process) => {
                const foldedProcessSettings = getFoldedProcessSettings(process);

                return (
                    <FoldingItem
                        key={process.label}
                        label={process.label}
                        count={process.occurrencesCount}
                        foldingStateProps={foldingStateProps}
                        foldingStateKey={getProcessId(process)}
                        padding={1}
                        searchString={searchString}
                        isUnfoldedByDefault={foldedProcessSettings.isUnfoldedByDefault}
                        declarationUrl={`/documents/edit/${process.documentAccession}/@${process.elementNodeId}`}
                    >
                        {foldedProcessSettings.renderedChildren.map((param) => {
                            const foldedParamSettings = getFoldedParamSettings(process, param);

                            return (
                                <FoldingItem
                                    key={param.label}
                                    label={param.label}
                                    foldingStateProps={foldingStateProps}
                                    foldingStateKey={getProcessParamId(process, param)}
                                    padding={2}
                                    searchString={searchString}
                                    isUnfoldedByDefault={foldedParamSettings.isUnfoldedByDefault}
                                >
                                    {foldedParamSettings.renderedChildren.map(
                                        (continuantStatRecord) => (
                                            <RightPanelListItem
                                                label={continuantStatRecord.label}
                                                count={continuantStatRecord.occurrencesCount}
                                                key={getProcessParamValueId(continuantStatRecord)}
                                                active={
                                                    // if empty graph graphViewNodeIds length is 0
                                                    !!continuantStatRecord.graphViewNodeIds
                                                        .length &&
                                                    continuantStatRecord.graphViewNodeIds.every(
                                                        (id) => selectedGraphNodes.includes(id)
                                                    )
                                                }
                                                setHoveredNodes={setHoveredNodes}
                                                selectGraphNodes={selectGraphNodes}
                                                graphViewNodeIds={
                                                    continuantStatRecord.graphViewNodeIds
                                                }
                                                padding={3}
                                                searchString={searchString}
                                                append={
                                                    <MatchedSynonym
                                                        continuant={continuantStatRecord}
                                                        searchValue={searchString}
                                                    />
                                                }
                                                declarationUrl={`/documents/edit/${continuantStatRecord.documentAccession}/@${continuantStatRecord.elementNodeId}`}
                                            />
                                        )
                                    )}

                                    {foldedParamSettings.hasShowMoreButton && (
                                        <ShowMoreButton
                                            isShowingMore={
                                                irrelevantShownProps.foldingState[
                                                    getProcessParamId(process, param)
                                                ]
                                            }
                                            level={3}
                                            onClick={() => {
                                                irrelevantShownProps.toggleFoldingState(
                                                    getProcessParamId(process, param)
                                                );
                                            }}
                                        />
                                    )}
                                </FoldingItem>
                            );
                        })}

                        {foldedProcessSettings.hasShowMoreButton && (
                            <ShowMoreButton
                                isShowingMore={
                                    irrelevantShownProps.foldingState[getProcessId(process)]
                                }
                                level={2}
                                onClick={() => {
                                    irrelevantShownProps.toggleFoldingState(getProcessId(process));
                                }}
                            />
                        )}
                    </FoldingItem>
                );
            })}
        </ScrollView>
    );
}
